import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Users, Mail, UserPlus, Check, X as XIcon, Crown, Eye, Edit3, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_ICONS = {
  owner: Crown,
  editor: Edit3,
  viewer: Eye,
};

const ROLE_COLORS = {
  owner: 'text-yellow-500',
  editor: 'text-blue-500',
  viewer: 'text-gray-500',
};

function CollaboratorsModal({ book, onClose }) {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [inviting, setInviting] = useState(false);

  const loadCollaborators = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/books/${book.book_id}/collaborators`);
      setCollaborators(response.data);
    } catch (error) {
      console.error('Failed to load collaborators:', error);
      toast.error('Failed to load collaborators');
    } finally {
      setLoading(false);
    }
  }, [book.book_id]);

  useEffect(() => {
    loadCollaborators();
  }, [book.book_id, loadCollaborators]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    try {
      const response = await axios.post(
        `${API}/books/${book.book_id}/collaborators`,
        { email: inviteEmail, role: inviteRole },
        
      );
      
      // Show appropriate message based on email status
      if (response.data.email_sent) {
        toast.success(`Invitation email sent to ${inviteEmail}`);
      } else if (response.data.email_status === 'skipped') {
        toast.success(`${inviteEmail} added as collaborator`, {
          description: 'Email service not configured - please notify them manually'
        });
      } else {
        toast.success(`${inviteEmail} added as collaborator`, {
          description: 'Email could not be sent - please notify them manually'
        });
      }
      
      setInviteEmail('');
      loadCollaborators();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send invitation';
      toast.error(msg);
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (collaboratorId, email) => {
    if (!window.confirm(`Remove ${email} from this book?`)) return;

    try {
      await axios.delete(`${API}/collaborators/${collaboratorId}`);
      toast.success('Collaborator removed');
      loadCollaborators();
    } catch (error) {
      toast.error('Failed to remove collaborator');
    }
  };

  const handleUpdateRole = async (collaboratorId, newRole) => {
    try {
      await axios.put(
        `${API}/collaborators/${collaboratorId}`,
        { role: newRole },
        
      );
      toast.success('Role updated');
      loadCollaborators();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-[#22201C] rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg sm:mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-[#2A2520]/50">
          <div className="flex items-center space-x-3">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#7C766E]" />
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-[#2A2420] dark:text-[#F0EBE4]">
                Writing Team
              </h2>
              <p className="text-xs sm:text-sm text-[#2A2420]/60 dark:text-[#F0EBE4]/60">
                {book.title}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 pb-8">
          {/* Invite Form */}
          <form onSubmit={handleInvite} className="space-y-3">
            <label className="block text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4]">
              Invite someone to help write
            </label>
            <div className="flex space-x-2">
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Enter email address"
                className="flex-1"
                data-testid="invite-email-input"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-[#2A2520] rounded-lg bg-white dark:bg-[#2A2520] text-sm"
                data-testid="invite-role-select"
              >
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
              <Button type="submit" disabled={inviting || !inviteEmail.trim()} data-testid="send-invite-button">
                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-[#2A2420]/50 dark:text-[#F0EBE4]/50">
              <strong>Editor:</strong> Can edit chapters • <strong>Viewer:</strong> Read-only access
            </p>
          </form>

          {/* Collaborators List */}
          <div>
            <h3 className="text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] mb-3">
              Team Members ({collaborators.length})
            </h3>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#7C766E]" />
              </div>
            ) : collaborators.length === 0 ? (
              <p className="text-center py-8 text-[#2A2420]/50 dark:text-[#F0EBE4]/50">
                No collaborators yet. Invite someone above!
              </p>
            ) : (
              <div className="space-y-2">
                {collaborators.map((collab, index) => {
                  const RoleIcon = ROLE_ICONS[collab.role] || Edit3;
                  const roleColor = ROLE_COLORS[collab.role] || 'text-blue-500';
                  const isOwner = collab.role === 'owner';
                  const isPending = collab.status === 'pending';

                  return (
                    <div
                      key={collab.collaborator_id || collab.email || index}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        isPending 
                          ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' 
                          : 'bg-[#F8F5F0] dark:bg-[#2A2520]/50'
                      }`}
                      data-testid={`collaborator-${collab.email}`}
                    >
                      <div className="flex items-center space-x-3">
                        {collab.picture ? (
                          <img src={collab.picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#8B7355]/20 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-[#7C766E]" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-[#2A2420] dark:text-[#F0EBE4]">
                            {collab.name || collab.email}
                          </p>
                          <div className="flex items-center space-x-2">
                            <RoleIcon className={`w-3 h-3 ${roleColor}`} />
                            <span className={`text-xs capitalize ${roleColor}`}>
                              {collab.role}
                            </span>
                            {isPending && (
                              <span className="text-xs bg-yellow-200 dark:bg-yellow-700 text-yellow-800 dark:text-yellow-200 px-2 py-0.5 rounded-full">
                                Pending
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {!isOwner && (
                        <div className="flex items-center space-x-2">
                          {!isPending && (
                            <select
                              value={collab.role}
                              onChange={(e) => handleUpdateRole(collab.collaborator_id, e.target.value)}
                              className="text-xs px-2 py-1 border border-gray-300 dark:border-[#2A2520] rounded bg-white dark:bg-[#2A2520]"
                            >
                              <option value="editor">Editor</option>
                              <option value="viewer">Viewer</option>
                            </select>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemove(collab.collaborator_id, collab.email)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <XIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CollaboratorsModal;
